import React, { useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, SelectChangeEvent } from '@mui/material';
import { displayFormatDateUTC } from '@converge/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { isDateOverdue } from '../utils/date-utils';
import {
  useGetUsers,
  UserGroup,
  useUpsertTaskMutation,
  TargetAreaType,
} from '@converge/graphql';
import { getUserOptions } from '../../../submission/underwriting-queue-grid';
import {
  useConvergeSnackBar,
  ConvergeNavigationUrls,
} from '../../../../contexts';
import { AssigneeDropdown } from './assignee-dropdown';
import './expanded-task-row.scss';

export const flexValuesClaimsExpanded = [1.9, 2, 1.2, 1.5, 1.1, 1.2, 1.3, 0.9];

export interface Task {
  id: string;
  uuid: string;
  taskName: string;
  status: string;
  dueDate: string;
  priority?: string;
  type?: string;
  note?: string;
  assignedUser?: string;
  userUuid?: string; // Added for tracking user assignments
}

interface ExpandedTaskRowProps {
  parentClaim: {
    uuid: string;
    referenceId: string;
    insured: string;
    claimant: string;
    createdAt: string;
  };
  tasks?: Task[];
}

export const ExpandedTaskRow: React.FC<ExpandedTaskRowProps> = ({
  parentClaim,
  tasks = [],
}) => {
  const { notify } = useConvergeSnackBar();

  // Fetch users from the Claims group for assignment dropdown
  const { data: claimUsersList } = useGetUsers({
    userGroups: [UserGroup.Claims],
  });

  const { upsertTask } = useUpsertTaskMutation({
    onError: (error: Error) => {
      console.error('Failed to update task owner:', error);
      notify(
        `Failed to update task owner: ${error.message || 'Unknown error'}`,
        {
          variant: 'error',
        }
      );
    },
  });

  // Convert user data to options format for dropdown
  const assigneeOptions = useMemo(() => {
    const options = getUserOptions(claimUsersList, false) || []; // UUID in value, name in label
    return options;
  }, [claimUsersList]);

  // Also get email options for API calls
  const assigneeEmailOptions = useMemo(() => {
    const emailOptions = getUserOptions(claimUsersList, true) || []; // Email in value, name in label
    return emailOptions;
  }, [claimUsersList]);

  // Create form methods for all tasks
  const defaultValues = tasks.reduce((acc, task) => {
    acc[`assignee-${task.uuid}`] = task.userUuid || '';
    return acc;
  }, {} as Record<string, string>);

  const formMethods = useForm({
    defaultValues,
  });

  // Update form values when tasks change to reflect current assignee values
  useEffect(() => {
    tasks.forEach((task) => {
      const fieldName = `assignee-${task.uuid}`;
      const currentValue = formMethods.getValues(fieldName);
      const expectedValue = task.userUuid || '';

      // Only set value if it's different from current value
      if (currentValue !== expectedValue) {
        formMethods.setValue(fieldName, expectedValue, { shouldDirty: false });
      }
    });
  }, [tasks, formMethods]);

  // Handler for updating task assignment - using same logic as diary page
  const handleUpdateTaskOwner = useCallback(
    async (event: SelectChangeEvent<unknown>, taskUuid: string) => {
      const userUuid = event?.target?.value as string;

      console.log('handleUpdateTaskOwner called with:', {
        userUuid,
        taskUuid,
        parentClaimUuid: parentClaim.uuid,
        event: event?.target?.value,
      });

      // Handle temporary tasks like diary page
      if (
        taskUuid === 'additional-task-default' ||
        taskUuid.startsWith('additional-task-')
      ) {
        console.log('Skipping temporary task update');
        return;
      }

      // Validate required fields (userUuid can be empty for unassigned tasks)
      if (!taskUuid || !parentClaim.uuid) {
        console.error('Missing required fields:', {
          userUuid,
          taskUuid,
          parentClaimUuid: parentClaim.uuid,
        });
        notify('Error: Missing required data for task update', {
          variant: 'error',
        });
        return;
      }

      // Find the user's email address from email options (handle empty userUuid for unassigned)
      const selectedUser = userUuid
        ? assigneeOptions.find((option) => option.value === userUuid)
        : null;
      const selectedEmailUser = selectedUser
        ? assigneeEmailOptions.find(
            (option) => option.label === selectedUser?.label
          )
        : null;
      const userEmail = selectedEmailUser?.value || userUuid || null; // null for unassigned tasks

      console.log('Selected user details:', {
        userUuid,
        selectedUser,
        selectedEmailUser,
        userEmail,
        assigneeOptions: assigneeOptions.slice(0, 3), // show first 3 for debugging
        emailOptions: assigneeEmailOptions.slice(0, 3), // show first 3 for debugging
      });

      notify('Saving task owner...', {
        variant: 'info',
      });

      try {
        console.log('Final mutation details:', {
          taskUuid: taskUuid,
          targetUuid: parentClaim.uuid,
          targetArea: TargetAreaType.Claim,
          ownerEmail: userEmail, // Now using actual email address
        });

        await upsertTask({
          variables: {
            details: {
              taskUuid: taskUuid,
              targetUuid: parentClaim.uuid,
              targetArea: TargetAreaType.Claim,
              ownerEmail: userEmail, // Use actual email address
            },
          },
        });

        notify('Task owner updated successfully', {
          variant: 'success',
        });
      } catch (e) {
        console.error('Error updating task owner:', e);
        notify(
          `Error updating task owner: ${
            e instanceof Error ? e.message : 'Unknown error'
          }`,
          {
            variant: 'error',
          }
        );
      }
    },
    [
      parentClaim.uuid,
      assigneeOptions,
      assigneeEmailOptions,
      notify,
      upsertTask,
    ]
  );

  if (!tasks || tasks.length === 0) {
    return (
      <Box className="no-tasks-container">
        <Typography variant="body1">No tasks found for this claim.</Typography>
      </Box>
    );
  }
  return (
    <FormProvider {...formMethods}>
      <Box className="expanded-task">
        {tasks.map((task, index) => (
          <Box
            className={`expanded-task-container ${
              isDateOverdue(task.dueDate, `task-row-${index}`)
                ? 'overdue'
                : 'normal'
            } ${index === 0 ? 'first-task' : 'subsequent-task'}`}
            key={task.id || `task-${index}`}
          >
            {/* Column 1: Task Name */}
            <Box
              className="task-name-column"
              style={{ flex: flexValuesClaimsExpanded[0] }}
            >
              <Box
                className="task-name-inner"
                style={{ flex: flexValuesClaimsExpanded[0] }}
              >
                TASK:{' '}
                <a
                  href={ConvergeNavigationUrls.claimEntityDetail({
                    claimEntityUuid: parentClaim.uuid,
                    sectionUrl: 'diary',
                  })}
                  className="task-link"
                >
                  {task.taskName}
                </a>
              </Box>
            </Box>

            {/* Column 2: Insured */}
            <Box
              className="empty-column"
              style={{ flex: flexValuesClaimsExpanded[1] }}
            ></Box>

            {/* Column 3: Claimant */}
            <Box
              className="empty-column"
              style={{ flex: flexValuesClaimsExpanded[2] }}
            ></Box>

            {/* Column 4: Assignee */}
            <Box
              className="assignee-column"
              style={{ flex: flexValuesClaimsExpanded[3] }}
            >
              <Box className="assignee-dropdown-container">
                <AssigneeDropdown
                  taskId={task.uuid}
                  options={assigneeOptions}
                  disabled={false}
                  onUpdateTaskOwner={handleUpdateTaskOwner}
                  isNotActive={false}
                />
              </Box>
            </Box>

            {/* Column 5: Reported */}
            <Box
              className="empty-column"
              style={{ flex: flexValuesClaimsExpanded[4] }}
            ></Box>

            {/* Column 6: Date of Loss */}
            <Box
              className="empty-column"
              style={{ flex: flexValuesClaimsExpanded[5] }}
            ></Box>

            {/* Column 7: Task Status */}
            <Box
              className="status-column"
              style={{ flex: flexValuesClaimsExpanded[6] }}
            ></Box>

            {/* Column 8: Due Date */}
            <Box
              className="due-date-column"
              style={{ flex: flexValuesClaimsExpanded[7] }}
            >
              <Box
                className={`due-date-text ${
                  isDateOverdue(task.dueDate, 'expanded-task-date')
                    ? 'overdue'
                    : 'normal'
                }`}
              >
                {displayFormatDateUTC(task.dueDate)}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </FormProvider>
  );
};

export default ExpandedTaskRow;
